import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Controller, useForm, type FieldValues, type Resolver } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { ApiError } from '../api/client';
import { Button } from '../design/primitives/Button';
import { Field } from '../design/primitives/Input';
import { Banner } from '../design/primitives/Misc';
import { FieldEditor, type Widgets } from '../fields/registry';
import { useWidgets } from '../api/context';
import type { FieldMeta } from '../meta/types';
import { defaultsFromMeta, normalizeValues, zodFromMeta } from './zodFromMeta';
import { blankFor } from '../fields/types';
import { deletePath, getPath, setPath } from '../meta/path';

// AutoForm: FieldMeta[] → a complete form. react-hook-form + zod from meta; fields grouped
// into sections by `group`; conditional fields via `conditions`; server Problem+JSON field
// errors mapped onto the matching inputs; edit mode submits only what changed.

export interface AutoFormProps {
  fields: FieldMeta[];
  mode: 'create' | 'edit';
  /** Initial values (edit: the row). */
  initial?: Record<string, unknown>;
  /** Receives normalized values (edit: only dirty fields). May throw ApiError. */
  onSubmit: (values: Record<string, unknown>, all: Record<string, unknown>) => Promise<unknown> | unknown;
  onCancel?: () => void;
  submitLabel?: string;
  cancelLabel?: string;
  busy?: boolean;
  /** Field visibility predicates keyed by field name. */
  conditions?: Record<string, (values: Record<string, unknown>) => boolean>;
  /** Extra widgets beyond the modules' (tests). */
  widgets?: Widgets;
  layout?: 'grid' | 'stack';
  /** Form element id — lets a footer outside the form submit it. */
  id?: string;
  hideActions?: boolean;
  /** Focus the first editable field on mount. Default: only for `mode: 'create'`. */
  autoFocus?: boolean;
  /** Extra content rendered above the actions (e.g. a note). */
  footer?: ReactNode;
  /** Field-name → full-width. */
  span?: Record<string, 1 | 2>;
  /**
   * Progressive disclosure: every group after the first starts collapsed behind "Show <group>"
   * unless it holds a required field or a value. The screens turn it on for create forms, so a
   * new record asks for the essentials and the rest is one click away, never a wall of fields.
   */
  collapseSecondary?: boolean;
  /**
   * Fields whose value comes from the surrounding context (the parent record of a related
   * list, a wizard step) — rendered read-only with their label and never autofocused, so a
   * create-in-context form shows "Client: Beta Ltd" instead of asking for the client again.
   */
  locked?: readonly string[];
  /** Called with the current values whenever they change (dependent lookups, mapped outcomes). */
  onValuesChange?: (values: Record<string, unknown>) => void;
  /** Values to write into the form (e.g. fields a lookup filled); applied when the object changes. */
  patch?: Record<string, unknown> | null;
}

export function AutoForm({ fields, mode, initial, onSubmit, onCancel, submitLabel, cancelLabel = 'Cancel', busy, conditions, widgets: extraWidgets, layout = 'grid', id, hideActions, autoFocus, footer, span, locked, collapseSecondary, onValuesChange, patch }: AutoFormProps) {
  // A create form takes focus: the person came to type. An edit form embedded in a screen (the
  // ticket inspector, a settings panel) must not — grabbing focus opens the first picker on load.
  const focusFirst = autoFocus ?? mode === 'create';
  const moduleWidgets = useWidgets();
  const widgets = useMemo(() => ({ ...moduleWidgets, ...extraWidgets }), [moduleWidgets, extraWidgets]);
  // A field hidden by its condition is neither validated nor submitted: a required field that does
  // not apply (a Halo type's "Version" when the maintenance type is not Upgrade) must not block.
  const shownFields = (vals: Record<string, unknown>) => (conditions ? fields.filter((f) => !conditions[f.name] || conditions[f.name]!(vals)) : fields);
  const schema = useMemo(() => zodFromMeta(fields), [fields]);
  // Reset only when the form's SHAPE (field names, defaults) or the initial values change — not when
  // a caller rebuilds equal field objects (e.g. new options for a dropdown), which would wipe input.
  const shapeKey = fields.map((f) => `${f.name}=${JSON.stringify(f.default ?? null)}`).join('|');
  // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed by shape on purpose (above)
  const defaults = useMemo(() => defaultsFromMeta(fields, initial), [shapeKey, initial]);
  const [formError, setFormError] = useState<string | null>(null);
  const [opened, setOpened] = useState<Set<string>>(() => new Set());

  const form = useForm<FieldValues>({
    resolver: (conditions
      ? (vals: FieldValues, ctx: unknown, opts: unknown) => zodResolver(zodFromMeta(shownFields(vals)) as never)(vals, ctx, opts as never)
      : zodResolver(schema as never)) as Resolver<FieldValues>,
    defaultValues: defaults,
    mode: 'onBlur',
    reValidateMode: 'onChange',
  });
  const { control, handleSubmit, formState, setError, reset, watch, setValue } = form;
  useEffect(() => {
    reset(defaults);
  }, [defaults, reset]);
  useEffect(() => {
    if (!onValuesChange) return;
    const sub = watch((v) => onValuesChange(v as Record<string, unknown>));
    return () => sub.unsubscribe();
  }, [watch, onValuesChange]);
  useEffect(() => {
    if (!patch) return;
    for (const [name, v] of Object.entries(patch)) setValue(name, v, { shouldDirty: true, shouldValidate: true });
  }, [patch, setValue]);

  const values = watch();

  const groups = useMemo(() => {
    const order: string[] = [];
    const map = new Map<string, FieldMeta[]>();
    for (const f of fields) {
      const g = f.group ?? '';
      if (!map.has(g)) {
        map.set(g, []);
        order.push(g);
      }
      map.get(g)!.push(f);
    }
    return order.map((g) => ({ name: g, fields: map.get(g)! }));
  }, [fields]);

  const submit = handleSubmit(async (all) => {
    setFormError(null);
    const shown = shownFields(all as Record<string, unknown>);
    const normalized = normalizeValues(shown, all as Record<string, unknown>, mode);
    let payload = normalized;
    if (mode === 'create') {
      // An untouched field still holding its blank (a boolean's `false`, an empty list) is
      // "not provided": drop it so the column's DB default applies (is_active true, sort 0…).
      // Values the caller supplied (`initial`) and anything the user touched are kept.
      const dirty = formState.dirtyFields as Record<string, unknown>;
      for (const f of fields) {
        if (getPath(dirty, f.name)) continue;
        if (initial && getPath(initial, f.name) !== undefined) continue;
        if (f.default !== undefined) continue;
        const v = getPath(normalized, f.name);
        const blank = blankFor(f);
        const same = v === blank || (Array.isArray(v) && Array.isArray(blank) && v.length === 0) || (v && typeof v === 'object' && !Array.isArray(v) && Object.keys(v as object).length === 0 && blank && typeof blank === 'object');
        if (same) deletePath(payload, f.name);
      }
    }
    if (mode === 'edit') {
      payload = {};
      for (const f of shown) {
        if (getPath(formState.dirtyFields as Record<string, unknown>, f.name)) setPath(payload, f.name, getPath(normalized, f.name));
      }
      if (Object.keys(payload).length === 0) {
        onCancel?.();
        return;
      }
    }
    try {
      await onSubmit(payload, normalized);
    } catch (e) {
      if (e instanceof ApiError) {
        let mapped = false;
        for (const [name, msgs] of Object.entries(e.fieldErrors)) {
          if (fields.some((f) => f.name === name)) {
            setError(name, { type: 'server', message: msgs.join(' ') });
            mapped = true;
          }
        }
        const extra = e.formErrors.join(' ');
        if (!mapped || extra) setFormError(extra || e.detail);
        return;
      }
      setFormError(e instanceof Error ? e.message : String(e));
    }
  });

  let firstFocusable = true;
  const isBusy = busy || formState.isSubmitting;
  // A secondary group stays folded until asked for, unless it must be answered now (a required
  // field) or already says something (a value the caller supplied).
  const hasValue = (f: FieldMeta) => {
    const v = getPath(initial ?? {}, f.name);
    return v !== undefined && v !== null && v !== '' && !(Array.isArray(v) && v.length === 0);
  };
  const folded = (name: string, i: number, gf: FieldMeta[]) => Boolean(collapseSecondary) && i > 0 && Boolean(name) && !opened.has(name) && !gf.some((f) => f.required || hasValue(f));

  return (
    <form id={id} onSubmit={submit} noValidate className="hf-autoform" aria-busy={isBusy || undefined}>
      {formError && (
        <div style={{ marginBottom: 'var(--s-4)' }}>
          <Banner tone="error">{formError}</Banner>
        </div>
      )}
      {groups.map((g, gi) => (
        <section key={g.name || '_'} className={groups.length > 1 || g.name ? 'hf-form-section' : undefined}>
          {folded(g.name, gi, g.fields) ? (
            <button type="button" className="hf-form-fold" onClick={() => setOpened((s) => new Set(s).add(g.name))} aria-expanded="false">
              <span className="hf-caps">{g.name}</span>
              <span className="hf-muted">Show {g.fields.length} more {g.fields.length === 1 ? 'field' : 'fields'}</span>
            </button>
          ) : g.name ? <div className="hf-caps">{g.name}</div> : null}
          {folded(g.name, gi, g.fields) ? null : (
          <div className={layout === 'grid' ? 'hf-form-grid' : 'hf-stack'}>
            {g.fields.map((f) => {
              if (conditions?.[f.name] && !conditions[f.name]!(values as Record<string, unknown>)) return null;
              const wide = span?.[f.name] ?? (f.type === 'textarea' || f.type === 'richtext' || f.type === 'json' || f.type === 'tags' ? 2 : 1);
              const isLocked = Boolean(locked?.includes(f.name));
              const focus = focusFirst && firstFocusable && !f.readOnly && !isLocked;
              if (focus) firstFocusable = false;
              const readOnly = isLocked || f.readOnly || (mode === 'edit' && !f.inUpdate) || (mode === 'create' && !f.inCreate);
              return (
                <Controller
                  key={f.name}
                  name={f.name}
                  control={control}
                  render={({ field: rhf, fieldState }) => (
                    <Field label={f.label} help={f.help} error={fieldState.error?.message} required={f.required} span={wide} disabled={isBusy || readOnly}>
                      <FieldEditor
                        field={f}
                        value={rhf.value}
                        onChange={(v) => rhf.onChange(v)}
                        row={{ ...(initial ?? {}), ...(values as Record<string, unknown>) }}
                        autoFocus={focus}
                        disabled={isBusy || readOnly}
                        invalid={Boolean(fieldState.error)}
                        widgets={widgets}
                        aria-label={f.label}
                      />
                    </Field>
                  )}
                />
              );
            })}
          </div>
          )}
        </section>
      ))}
      {footer}
      {!hideActions && (
        <div className="hf-form-actions">
          {onCancel && (
            <Button variant="ghost" onClick={onCancel} disabled={isBusy}>
              {cancelLabel}
            </Button>
          )}
          <Button type="submit" variant="primary" busy={isBusy}>
            {submitLabel ?? (mode === 'create' ? 'Create' : 'Save changes')}
          </Button>
        </div>
      )}
    </form>
  );
}
